import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '@canvas-logic/clp-shared-ui';

import { useBootstrapper } from '@/ui-kit/contexts/BootstrapperContext';

import type { LanguageIssue } from './languageFallback';

interface Props {
  issue: LanguageIssue | null;
}

/**
 * Tells the user their requested language was unavailable. Must render inside
 * both the localization provider and the bootstrapper provider.
 */
export function LanguageFallbackNotice({ issue }: Props): null {
  const { bootstrapper } = useBootstrapper();
  const { resolvedLanguage } = useLocalization();
  const { t } = useTranslation();
  const notifiedFor = useRef<string | null>(null);

  useEffect(() => {
    const notificationService = bootstrapper?.notificationService;
    // Bootstrapper inits asynchronously; no resolved language means the
    // fallback request is still in flight and has no name to show yet.
    if (!issue || !notificationService || !resolvedLanguage) return;
    if (notifiedFor.current === issue.requested) return;
    notifiedFor.current = issue.requested;

    const key = issue.fallbackApplied ? 'errors.languageNotAvailableFallback' : 'errors.languageNotAvailableKept';
    void notificationService.info(
      t(key, { requested: issue.requested.toUpperCase(), fallback: resolvedLanguage.toUpperCase() }),
    );
  }, [issue, bootstrapper, resolvedLanguage, t]);

  return null;
}
